import { useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Send, Plus, X, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_BODY = 2000;

const emptyForm = { to: [""], cc: [], bcc: [], subject: "", body: "" };

function RecipientRows({ label, values, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          {label}
        </span>
        <button
          type="button"
          data-testid={`add-${testid}`}
          onClick={() => onChange([...values, ""])}
          className="text-[10px] font-mono text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
        >
          <Plus size={11} /> ADD
        </button>
      </div>
      <div className="mt-1 space-y-1">
        {values.length === 0 && (
          <div className="text-[11px] text-zinc-600 font-mono">—</div>
        )}
        {values.map((v, i) => (
          <div key={i} className="flex gap-1">
            <input
              data-testid={`${testid}-input-${i}`}
              type="email"
              value={v}
              placeholder="name@domain.com"
              onChange={(e) => {
                const next = [...values];
                next[i] = e.target.value;
                onChange(next);
              }}
              className="flex-1 h-8 bg-[#0E0E0E] border border-[#27272A] focus:border-amber-400 outline-none px-2 text-[12px] font-mono text-white"
            />
            <button
              type="button"
              data-testid={`remove-${testid}-${i}`}
              onClick={() => onChange(values.filter((_, k) => k !== i))}
              className="h-8 w-8 border border-[#27272A] text-zinc-500 hover:text-rose-400 flex items-center justify-center"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function EmailPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [activeThread, setActiveThread] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: threadsData } = useQuery({
    queryKey: ["email-threads"],
    queryFn: () =>
      axios.get(`${API}/api/emails/threads`, { withCredentials: true }).then((r) => r.data),
  });

  const { data: threadDetail } = useQuery({
    queryKey: ["email-thread", activeThread],
    enabled: !!activeThread,
    queryFn: () =>
      axios
        .get(`${API}/api/emails/thread/${activeThread}`, { withCredentials: true })
        .then((r) => r.data),
  });

  const send = useMutation({
    mutationFn: () => {
      const payload = {
        to: form.to.map((s) => s.trim()).filter(Boolean),
        cc: form.cc.map((s) => s.trim()).filter(Boolean),
        bcc: form.bcc.map((s) => s.trim()).filter(Boolean),
        subject: form.subject.trim(),
        body: form.body,
      };
      if (activeThread) payload.thread_id = activeThread;
      return axios
        .post(`${API}/api/emails/send`, payload, { withCredentials: true })
        .then((r) => r.data);
    },
    onSuccess: (d) => {
      if (d.sent) toast.success("EMAIL_SENT · via SendGrid (TLS)");
      else
        toast.message("EMAIL_QUEUED", {
          description: "Saved to your thread. Delivery activates once SendGrid is configured.",
        });
      setForm(emptyForm);
      setActiveThread(d.thread_id);
      qc.invalidateQueries({ queryKey: ["email-threads"] });
      qc.invalidateQueries({ queryKey: ["email-thread", d.thread_id] });
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((x) => x.msg).join(" ") : detail;
      toast.error(msg || "SEND_FAILED");
    },
  });

  const canSend =
    form.to.some((s) => s.trim()) && form.subject.trim() && form.body.trim() && !send.isPending;

  const threads = threadsData?.threads || [];

  return (
    <div data-testid="email-page" className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Mail size={16} className="text-amber-400" />
        <h1 className="text-sm font-mono uppercase tracking-wider text-white">Compose &amp; Send Email</h1>
      </div>

      <div className="border border-emerald-400/30 bg-emerald-500/[0.05] px-3 py-2 text-[11px] font-mono text-emerald-200 flex items-center gap-2">
        <ShieldCheck size={13} />
        SENT FROM <span className="text-white">support@themindstein.com</span> · TLS ENCRYPTED · SENDER LOCKED
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* compose */}
        <div className="lg:col-span-7 border border-[#27272A] bg-[#0A0A0A] p-4 space-y-3">
          {activeThread && (
            <div className="flex items-center justify-between text-[10px] font-mono text-amber-300">
              <span>REPLYING IN THREAD · {activeThread.slice(0, 8)}</span>
              <button
                data-testid="new-thread-button"
                onClick={() => { setActiveThread(null); setForm(emptyForm); }}
                className="text-zinc-500 hover:text-white"
              >
                NEW_THREAD
              </button>
            </div>
          )}
          <RecipientRows label="TO" values={form.to} onChange={(v) => set("to", v)} testid="to" />
          <RecipientRows label="CC" values={form.cc} onChange={(v) => set("cc", v)} testid="cc" />
          <RecipientRows label="BCC" values={form.bcc} onChange={(v) => set("bcc", v)} testid="bcc" />

          <div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">SUBJECT</span>
            <input
              data-testid="email-subject"
              value={form.subject}
              maxLength={255}
              onChange={(e) => set("subject", e.target.value)}
              className="mt-1 w-full h-9 bg-[#0E0E0E] border border-[#27272A] focus:border-amber-400 outline-none px-2 text-[13px] font-mono text-white"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">MESSAGE</span>
              <span
                data-testid="char-counter"
                className={`text-[10px] font-mono ${form.body.length > MAX_BODY ? "text-rose-400" : "text-zinc-500"}`}
              >
                {form.body.length}/{MAX_BODY}
              </span>
            </div>
            <textarea
              data-testid="email-body"
              value={form.body}
              maxLength={MAX_BODY}
              rows={9}
              onChange={(e) => set("body", e.target.value)}
              className="mt-1 w-full bg-[#0E0E0E] border border-[#27272A] focus:border-amber-400 outline-none px-2 py-2 text-[13px] text-zinc-100 leading-relaxed resize-y"
            />
          </div>

          <button
            data-testid="send-email-button"
            disabled={!canSend}
            onClick={() => send.mutate()}
            className="w-full h-10 bg-amber-400 text-black font-mono uppercase text-[12px] tracking-wider flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-amber-300 transition-colors"
          >
            <Send size={14} /> {send.isPending ? "SENDING…" : "SEND"}
          </button>
        </div>

        {/* chain history */}
        <div className="lg:col-span-5 space-y-3">
          <div className="border border-[#27272A] bg-[#0A0A0A]">
            <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
              THREAD_HISTORY
            </div>
            <div data-testid="thread-list" className="divide-y divide-[#161616] max-h-52 overflow-auto">
              {threads.length === 0 && (
                <div className="px-3 py-4 text-[11px] text-zinc-600 font-mono">No emails yet.</div>
              )}
              {threads.map((t) => (
                <button
                  key={t.thread_id}
                  data-testid={`thread-${t.thread_id}`}
                  onClick={() => setActiveThread(t.thread_id)}
                  className={`w-full text-left px-3 py-2 hover:bg-[#111] ${activeThread === t.thread_id ? "bg-[#111] border-l-2 border-amber-400" : ""}`}
                >
                  <div className="text-[12px] text-white font-mono truncate">{t.subject}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">
                    {t.count} msg · {(t.recipients || []).join(", ").slice(0, 40)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {threadDetail && (
            <div data-testid="thread-detail" className="border border-[#27272A] bg-[#0A0A0A]">
              <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                CHAIN · {threadDetail.messages.length} MESSAGE(S)
              </div>
              <div className="divide-y divide-[#161616] max-h-80 overflow-auto">
                {threadDetail.messages.map((m) => (
                  <div key={m.id} className="px-3 py-2 space-y-1">
                    <div className="text-[10px] font-mono text-zinc-500">
                      {m.from_email} → {m.to.join(", ")}
                      {m.cc?.length ? ` · cc: ${m.cc.join(", ")}` : ""}
                    </div>
                    <div className="text-[12px] text-white font-mono">{m.subject}</div>
                    <div className="text-[12px] text-zinc-300 whitespace-pre-wrap leading-relaxed">{m.body}</div>
                    <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-600">
                      {m.status} · {new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
